import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type { ChannelRateLimitsResponse, UpdateChannelRateLimitsRequest } from '@troia-v3/types';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons';
import { useUpdateChannelRateLimits, useRestoreChannelRateLimits } from '../../hooks';

/**
 * Subtítulo do modal, consciente do provider — evita falar de "bloqueio do
 * WhatsApp" num canal de e-mail (achado da revisão da Task 9: o card de
 * limites passou a existir também no e-mail, e o texto era herdado do
 * WhatsApp verbatim). Mapa central em vez de `if` espalhado pelo JSX;
 * qualquer providerId fora do mapa cai no texto neutro.
 */
const RATE_LIMITS_SUBTITLE_BY_PROVIDER: Record<string, string> = {
  'gateway-whatsapp': 'Define o ritmo máximo de envio para este canal. Use valores conservadores para evitar bloqueio do WhatsApp.',
  'whatsapp-business': 'Define o ritmo máximo de envio para este canal. Use valores conservadores para evitar bloqueio do WhatsApp.',
  'email-resend': 'Controla a velocidade de envio de e-mails. Ritmo muito alto pode prejudicar a reputação do domínio; ritmo baixo faz campanhas grandes levarem mais tempo.',
};

const DEFAULT_RATE_LIMITS_SUBTITLE = 'Controla a velocidade de envio deste canal.';

interface RateLimitsModalProps {
  channelId: string;
  isOpen: boolean;
  onClose: () => void;
  current: ChannelRateLimitsResponse;
}

export const RateLimitsModal: React.FC<RateLimitsModalProps> = ({ channelId, isOpen, onClose, current }) => {
  const updateMutation = useUpdateChannelRateLimits(channelId);
  const restoreMutation = useRestoreChannelRateLimits(channelId);

  const supportsAutoTier = current.meta.supportsAutoTier;
  const initialIntervalSeconds = current.rateLimits.messagesPerHour > 0
    ? Math.round(3600 / current.rateLimits.messagesPerHour)
    : 0;

  const initialConversationIntervalSeconds = Math.max(1, Math.round((current.rateLimits.conversationMinDelayMs ?? 3000) / 1000));
  const initialConversationPerHour = current.rateLimits.conversationMessagesPerHour ?? 300;

  const [intervalSeconds, setIntervalSeconds] = useState<number>(initialIntervalSeconds);
  const [messagesPerDay, setMessagesPerDay] = useState<number>(current.rateLimits.messagesPerDay);
  const [conversationIntervalSeconds, setConversationIntervalSeconds] = useState<number>(initialConversationIntervalSeconds);
  const [conversationPerHour, setConversationPerHour] = useState<number>(initialConversationPerHour);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    setIntervalSeconds(initialIntervalSeconds);
    setMessagesPerDay(current.rateLimits.messagesPerDay);
    setConversationIntervalSeconds(initialConversationIntervalSeconds);
    setConversationPerHour(initialConversationPerHour);
  }, [current, initialIntervalSeconds, initialConversationIntervalSeconds, initialConversationPerHour]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      const body: UpdateChannelRateLimitsRequest = {};
      if (intervalSeconds !== initialIntervalSeconds && intervalSeconds >= 1) {
        body.intervalSeconds = intervalSeconds;
      }
      if (!supportsAutoTier && messagesPerDay !== current.rateLimits.messagesPerDay && messagesPerDay >= 1) {
        body.messagesPerDay = messagesPerDay;
      }
      if (conversationIntervalSeconds !== initialConversationIntervalSeconds && conversationIntervalSeconds >= 1) {
        body.conversationIntervalSeconds = conversationIntervalSeconds;
      }
      if (conversationPerHour !== initialConversationPerHour && conversationPerHour >= 1) {
        body.conversationMessagesPerHour = conversationPerHour;
      }
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      await updateMutation.mutateAsync(body);
      toast.success('Limites atualizados.');
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erro ao atualizar limites.';
      toast.error(message);
    }
  };

  const handleRestore = async () => {
    try {
      await restoreMutation.mutateAsync();
      toast.success('Limites restaurados para o padrão automático.');
      setShowRestoreConfirm(false);
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erro ao restaurar limites.';
      toast.error(message);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* config: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="rate-limits-modal"
    >
      <div
        className="w-full max-w-md p-6 rounded-lg shadow-xl space-y-4"
        style={{
          backgroundColor: '#ffffff',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-card-text': '#334155',
          '--theme-card-muted': '#94a3b8',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-primary': '#8b5cf6',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
            Limites de envio
          </h3>
          <p className="text-sm mt-1" style={{ color: '#6b7280' }}>
            {RATE_LIMITS_SUBTITLE_BY_PROVIDER[current.meta.providerId] ?? DEFAULT_RATE_LIMITS_SUBTITLE}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
              Intervalo mínimo entre mensagens (segundos)
            </label>
            <Input
              type="number"
              min={1}
              max={86400}
              value={intervalSeconds}
              onChange={(e) => setIntervalSeconds(Number((e.target as HTMLInputElement).value))}
              data-testid="rate-limits-modal-interval-input"
            />
            <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
              Equivalente a {Math.ceil(3600 / Math.max(intervalSeconds, 1))} mensagens por hora.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
              Limite diário
              {supportsAutoTier && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                  Atualizado pela Meta
                </span>
              )}
            </label>
            <Input
              type="number"
              min={1}
              max={10_000_000}
              value={messagesPerDay}
              disabled={supportsAutoTier}
              onChange={(e) => setMessagesPerDay(Number((e.target as HTMLInputElement).value))}
              data-testid="rate-limits-modal-daily-input"
            />
            {supportsAutoTier && (
              <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                A Meta define este valor automaticamente baseado no tier da sua conta.
              </p>
            )}
          </div>

          <div className="pt-2" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: '#1e293b' }}>Mensagens de automação (workflow, follow-up, agente)</p>
            <p className="text-xs mb-3" style={{ color: '#6b7280' }}>
              Ritmo próprio, separado das campanhas. Protege o canal quando muitos fluxos disparam ao mesmo tempo.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
              Intervalo mínimo entre mensagens de automação (segundos)
            </label>
            <Input
              type="number"
              min={1}
              max={3600}
              value={conversationIntervalSeconds}
              onChange={(e) => setConversationIntervalSeconds(Number((e.target as HTMLInputElement).value))}
              data-testid="rate-limits-modal-conversation-interval-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
              Máximo de mensagens de automação por hora
            </label>
            <Input
              type="number"
              min={1}
              max={100000}
              value={conversationPerHour}
              onChange={(e) => setConversationPerHour(Number((e.target as HTMLInputElement).value))}
              data-testid="rate-limits-modal-conversation-hour-input"
            />
          </div>

          <div className="text-xs p-3 rounded" style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
            Hoje: <strong>{current.currentUsage.sentToday}</strong> de{' '}
            <strong>{current.currentUsage.dailyLimit}</strong> enviados.
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRestoreConfirm(true)}
            disabled={restoreMutation.isPending || updateMutation.isPending}
            data-testid="rate-limits-modal-restore-btn"
          >
            <Icon name="refresh" className="w-4 h-4 mr-1" />
            Restaurar automático
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={restoreMutation.isPending || updateMutation.isPending}
              data-testid="rate-limits-modal-close-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={restoreMutation.isPending || updateMutation.isPending}
              data-testid="rate-limits-modal-save-btn"
            >
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>

        {showRestoreConfirm && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            /* confirmação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
            data-testid="rate-limits-modal-restore-confirm"
          >
            <div
              className="w-full max-w-sm p-5 rounded-lg space-y-3"
              style={{ backgroundColor: '#ffffff' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-base font-semibold" style={{ color: '#1e293b' }}>
                Restaurar gerenciamento automático?
              </h4>
              <p className="text-sm" style={{ color: '#6b7280' }}>
                Os limites manuais serão apagados. O canal voltará a usar o padrão do provider
                {supportsAutoTier ? ' até o próximo evento da Meta atualizar o tier' : ''}.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowRestoreConfirm(false)}
                  data-testid="rate-limits-modal-restore-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleRestore}
                  disabled={restoreMutation.isPending}
                  data-testid="rate-limits-modal-restore-confirm-btn"
                >
                  {restoreMutation.isPending ? 'Restaurando...' : 'Restaurar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ), document.body);
};

RateLimitsModal.displayName = 'RateLimitsModal';
